import { useEffect } from "react";
import { pick } from "lodash";
import { useHistory, useParams } from "react-router";
import { buildLink } from "common/routing";
import {
  GetPropertyForPropertyEditDocument,
  Property,
  useAdminMergePropertyMutation,
  useAdminUpdatePropertyMutation,
  useGetPropertyForPropertyEditQuery,
} from "../../../../../generated/graphql";
import { useStatusToasts } from "../../../../../hooks/useStatusToasts";
import { useSimpleForm } from "../../../../Common/simpleForm";
import { AppContainer } from "./__styles__/EditProperty";
import PropertyEditForm from "./PropertyEditForm";

type InputType = Pick<
  Property,
  "id" | "city" | "streetAddress" | "zipcode" | "state" | "parcel"
> & { ignoreParcel: boolean; latitude: number; longitude: number };

const sanitizeInput = (input: InputType) => {
  const sanitizedInput = pick(input, [
    "id",
    "latitude",
    "longitude",
    "city",
    "streetAddress",
    "zipcode",
    "ignoreParcel",
    "state",
  ]);

  return {
    ...sanitizedInput,
    geocacheParcelId: input.parcel?.id,
  };
};

const EditProperty = () => {
  const { propertyId } = useParams<{ propertyId: string }>();
  const history = useHistory();

  const { addSuccessToast, addErrorToast } = useStatusToasts();
  const [input, get, set] = useSimpleForm();

  const { data, loading, error } = useGetPropertyForPropertyEditQuery({
    variables: { id: propertyId },
  });

  const refetchQueries = [
    {
      query: GetPropertyForPropertyEditDocument,
      variables: { id: propertyId },
    },
  ];

  const onError = (gerund: string) => (error: Error) => {
    addErrorToast(`There was an issue ${gerund} the property: ${error}`);
  };

  const onCompleted = (pastParticiple: string) => () => {
    addSuccessToast(`Property ${pastParticiple}!`);
  };

  const [savePropertyMutation, { loading: processingUpdate }] =
    useAdminUpdatePropertyMutation({
      refetchQueries,
      onError: onError("saving"),
      onCompleted: onCompleted("saved"),
    });

  const [mergePropertyMutation, { loading: processingMerge }] =
    useAdminMergePropertyMutation({
      refetchQueries,
      onError: onError("merging"),
      onCompleted: onCompleted("merged"),
    });

  useEffect(() => {
    if (processingUpdate || processingMerge || !data?.getProperty) return;

    for (const [key, val] of Object.entries(data.getProperty)) {
      set(key)(val);
    }
  }, [data, processingUpdate, processingMerge, set]);

  if (loading || error || !data?.getProperty) {
    return <div />;
  }

  const account = data.getProperty.account;

  const property = data.getProperty;

  const onSaveProperty = async () => {
    const response = await savePropertyMutation({
      variables: { data: sanitizeInput(input as InputType) },
    });

    const nextId = response.data?.updateProperty.id;
    if (nextId) {
      history.replace(buildLink("adminPropertyEdit", { propertyId: nextId }));
    }
  };

  const onMergeProperty = async (childId: string) => {
    const response = await mergePropertyMutation({
      variables: { data: { parentId: propertyId, childIds: [childId] } },
    });

    const nextId = response.data?.mergeProperty.id;
    if (nextId) {
      history.replace(buildLink("adminPropertyEdit", { propertyId: nextId }));
    }
  };

  return (
    <AppContainer>
      {property && (
        <PropertyEditForm
          set={set}
          get={get}
          property={data!.getProperty}
          account={account}
          processing={processingMerge || processingUpdate}
          saveProperty={onSaveProperty}
          mergeProperty={onMergeProperty}
        />
      )}
    </AppContainer>
  );
};
export default EditProperty;
